import React from "react";
import { ActionIcon, Menu, Text, TextInput } from "@mantine/core";
import { IconSearch, IconX } from "@tabler/icons-react";
import FilterChip from "../../common/FilterChip";
import styles from "./sales.module.css";

/**
 * Shared toolbar primitives for the Sales tabs (Transactions, Payments, Gift
 * Cards). These are the repeatable design-system pieces: the pill search field,
 * the applied-filter pills (a select-style `MenuToken` and a dismissible
 * `TextToken`), and the result count. Each tab composes them with its own
 * `+ Filter` popover for its own filter set, so the tabs look and behave the
 * same while keeping their distinct filters.
 *
 * The pill visuals were promoted to the app-wide `common/FilterChip`; the two
 * tokens below are thin Sales-specific wrappers over it (same props and
 * accessible names as before).
 */

interface SalesSearchInputProps {
  value: string;
  onChange: (value: string) => void;
  placeholder: string;
}

/** The rounded pill search field with a leading magnifier and a clear button. */
export function SalesSearchInput({
  value,
  onChange,
  placeholder,
}: SalesSearchInputProps) {
  return (
    <TextInput
      value={value}
      onChange={(e) => onChange(e.currentTarget.value)}
      placeholder={placeholder}
      aria-label={placeholder}
      radius={999}
      leftSection={<IconSearch size={15} stroke={2.2} />}
      rightSection={
        value ? (
          <ActionIcon
            variant="transparent"
            color="gray"
            aria-label="Clear search"
            onClick={() => onChange("")}
          >
            <IconX size={13} stroke={2.4} />
          </ActionIcon>
        ) : null
      }
      styles={{
        root: { flex: "0 1 270px", minWidth: 220 },
        input: { height: 34, minHeight: 34, borderColor: "#cbcbcb" },
      }}
    />
  );
}

interface MenuTokenProps {
  label: string;
  valueLabel: string;
  current: string;
  options: { value: string; label: string }[];
  onPick: (value: string) => void;
}

/** A select-style filter pill: click it to change the value inline. */
export function MenuToken({
  label,
  valueLabel,
  current,
  options,
  onPick,
}: MenuTokenProps) {
  return (
    <Menu position="bottom-start" withinPortal shadow="md" radius="md">
      <Menu.Target>
        <FilterChip
          label={label}
          value={valueLabel}
          aria-label={`Change ${label} filter`}
        />
      </Menu.Target>
      <Menu.Dropdown>
        {options.map((o) => (
          <Menu.Item
            key={o.value}
            onClick={() => onPick(o.value)}
            fw={current === o.value ? 700 : 400}
          >
            {o.label}
          </Menu.Item>
        ))}
      </Menu.Dropdown>
    </Menu>
  );
}

interface TextTokenProps {
  label: string;
  value: string;
  onEdit: () => void;
  onRemove: () => void;
}

/** A text filter pill: click the body to edit (opens the popover), ✕ to clear. */
export function TextToken({ label, value, onEdit, onRemove }: TextTokenProps) {
  return (
    <FilterChip
      label={label}
      value={value}
      onClick={onEdit}
      onRemove={onRemove}
      removeLabel={`Remove ${label} filter`}
    />
  );
}

interface ResultCountProps {
  count: number;
  total: number;
  /** Noun for the result count, e.g. "transactions" / "gift cards". */
  itemLabel: string;
}

/** The right-aligned "N of M items" result count. */
export function ResultCount({ count, total, itemLabel }: ResultCountProps) {
  return (
    <Text className={styles.resultCount}>
      {count} of {total} {itemLabel}
    </Text>
  );
}
